import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { withRouter } from '../../../common/withRouter';
import { ButtonOBSOLETED, DataDefinitionList, Loader, Notification, Message } from "../../../web.common";
import { LayoutContext } from "../../layout/siteLayout";
import PageTop from "../../layout/PageTop";
import withApi from "../../higherOrderComponents/withApi";
import { DisplayDate } from "../../items";
import {
  apiErrorText,
  buttonRowStyle,
  externalLink,
  formatMinor,
  linkOrText,
  panelStyle,
  warningTextStyle
} from "./shared";

const reasonMaxLength = 300;

// Wanna detail with the per-player payment state and the two wanna actions. Both actions are
// explicit buttons that open a confirmation panel first; nothing runs on page load.
const WannaInternal = ({ api, match: { params: { code } } }) => {
  const [data, setData] = useState(null);
  const [notification, setNotification] = useState(null);
  const [panel, setPanel] = useState(null); // "cancel" | "delete" | null
  const [reason, setReason] = useState("");
  const [isBusy, setIsBusy] = useState(false);

  const loadData = async () => {
    try {
      setData(await api.clubhouseAdmin.getWanna(code));
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to load the wanna.")));
    }
  };

  useEffect(() => {
    loadData();
  }, [code]);

  const openPanel = (name) => {
    setReason("");
    setPanel(name);
  };

  const closePanel = () => {
    setPanel(null);
    setReason("");
  };

  const cancelGame = async () => {
    setIsBusy(true);
    try {
      const result = await api.clubhouseAdmin.cancelWanna(code, reason.trim());
      const text = `Game cancelled: ${result.refundedOrders} order(s) refunded, ${result.failedRefunds} refund(s) failed.`;
      setNotification(result.failedRefunds > 0 ? new Message("warning", text) : new Message("success", text));
      closePanel();
      loadData();
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to cancel the game.")));
    } finally {
      setIsBusy(false);
    }
  };

  const deleteWanna = async () => {
    setIsBusy(true);
    try {
      await api.clubhouseAdmin.deleteWanna(code, reason.trim());
      setNotification(new Message("success", "Wanna deleted."));
      closePanel();
      loadData();
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to delete the wanna.")));
    } finally {
      setIsBusy(false);
    }
  };

  const wanna = data?.wanna;
  const participants = data?.participants || [];
  const orders = data?.orders || [];
  const settledOrders = orders.filter(({ status }) => status === "completed");

  const ordersOf = (userId) => orders.filter((order) => order.userId === userId);

  const listData = wanna && {
    Code: wanna.code,
    Clubhouse: linkOrText(`/clubhouse/${encodeURIComponent(wanna.clubhouseSlug)}`, wanna.clubhouseSlug),
    Form: wanna.form,
    Status: wanna.status,
    Sport: wanna.sport || "-",
    Text: wanna.text || "-",
    Creator: wanna.creator ? `${wanna.creator.name} (${wanna.creator.email})` : "-",
    "Share per player": formatMinor(wanna.shareMinor, wanna.currencyIsoCode),
    Paid: formatMinor(wanna.paidMinor ?? 0, wanna.currencyIsoCode),
    Refunded: formatMinor(wanna.refundedMinor ?? 0, wanna.currencyIsoCode),
    "Joined / interested": `${wanna.joinedCount} / ${wanna.interestedCount}`,
    "Starts At": <DisplayDate>{wanna.startsAtUtc}</DisplayDate>,
    Schedule: wanna.scheduleText || "-",
    "Created At": <DisplayDate>{wanna.createdAtUtc}</DisplayDate>,
    "Cancelled At": wanna.cancelledAtUtc ? <DisplayDate>{wanna.cancelledAtUtc}</DisplayDate> : "-",
    "Cancel reason": wanna.cancelReason || "-",
    "Deleted At": wanna.deletedAtUtc ? <DisplayDate>{wanna.deletedAtUtc}</DisplayDate> : "-",
    "Delete reason": wanna.deleteReason || "-",
    Demo: wanna.isDemo ? "yes" : "no",
  };

  const isCancelled = wanna?.status === "cancelled";
  const isDeleted = !!wanna?.deletedAtUtc;

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <div className="content-with-right-pane-delimiter">
            <PageTop documentTitle="Clubhouse wanna" documentDescription="Clubhouse wanna" />
            <h2 className="main-page-header main-page-header-with-subheader">Clubhouse wanna</h2>
            <h5 className="main-page-subheader">{code}</h5>
            <br />

            {!data ? <Loader hasLoaded={false}>Loading...</Loader> : (
              <React.Fragment>
                <DataDefinitionList listData={listData} classNames={["detail-page-list"]} />

                <h3>Actions</h3>
                <div style={buttonRowStyle}>
                  <ButtonOBSOLETED
                    buttonContent="Cancel game and refund"
                    disabled={isBusy || isCancelled || isDeleted || panel !== null}
                    onClick={() => openPanel("cancel")}
                  />
                  <ButtonOBSOLETED
                    buttonContent="Delete wanna"
                    disabled={isBusy || isDeleted || panel !== null}
                    onClick={() => openPanel("delete")}
                  />
                </div>
                {isCancelled && <p>The game is already cancelled.</p>}

                {panel === "cancel" && (
                  <div style={panelStyle}>
                    <h3>Cancel this game and refund?</h3>
                    <p>
                      The wanna {code} becomes <strong>cancelled</strong>. Every settled charge is refunded in full:
                      {" "}{settledOrders.length} settled order(s), {formatMinor(wanna.paidMinor ?? 0, wanna.currencyIsoCode)} paid
                      {" "}({formatMinor(wanna.refundedMinor ?? 0, wanna.currencyIsoCode)} already refunded).
                      Refunds go to Frisbii; refunds that fail are counted and can be retried from the Payments page.
                    </p>
                    <label>
                      Reason (required, max {reasonMaxLength} characters)<br />
                      <input
                        style={{ width: "100%" }}
                        maxLength={reasonMaxLength}
                        value={reason}
                        onChange={({ target }) => setReason(target.value)}
                      />
                    </label>
                    <div style={buttonRowStyle}>
                      <ButtonOBSOLETED buttonContent="Confirm cancel and refund" disabled={isBusy || reason.trim() === ""} onClick={cancelGame} />
                      <ButtonOBSOLETED buttonContent="Back" disabled={isBusy} onClick={closePanel} />
                    </div>
                  </div>
                )}

                {panel === "delete" && (
                  <div style={panelStyle}>
                    <h3>Delete this wanna?</h3>
                    <p>
                      The wanna {code} is <strong>soft-deleted</strong>: it disappears from the clubhouse and its lists,
                      the row is kept with a deleted date.
                    </p>
                    <p style={warningTextStyle}>
                      Refused if a charge of this wanna is settled and not refunded: in that case cancel the game
                      first (it refunds), then delete.
                    </p>
                    <label>
                      Reason (optional, max {reasonMaxLength} characters)<br />
                      <input
                        style={{ width: "100%" }}
                        maxLength={reasonMaxLength}
                        value={reason}
                        onChange={({ target }) => setReason(target.value)}
                      />
                    </label>
                    <div style={buttonRowStyle}>
                      <ButtonOBSOLETED buttonContent="Confirm delete" disabled={isBusy} onClick={deleteWanna} />
                      <ButtonOBSOLETED buttonContent="Back" disabled={isBusy} onClick={closePanel} />
                    </div>
                  </div>
                )}

                <h3>Participants and payments</h3>
                {participants.length === 0 ? <p>None</p> : (
                  <article className="data-table-wrap">
                    <table className="data-lists component-data-table cell-content-align-top">
                      <thead>
                        <tr>
                          <td>Name</td>
                          <td>Email</td>
                          <td>Participant status</td>
                          <td>Share</td>
                          <td>Orders of this player</td>
                        </tr>
                      </thead>
                      <tbody>
                        {participants.map((participant) => (
                          <tr key={participant.userId}>
                            <td>{participant.name}</td>
                            <td>{participant.email}</td>
                            <td>{participant.status}</td>
                            <td>{formatMinor(wanna.shareMinor, wanna.currencyIsoCode)}</td>
                            <td>
                              {ordersOf(participant.userId).length === 0 ? "no order" : ordersOf(participant.userId).map((order) => (
                                <div key={order.orderHandle}>
                                  {order.status}, {formatMinor(order.amountMinor, order.currencyIsoCode)} {externalLink(order.frisbiiUrl)}
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </article>
                )}

                <h3>Orders ({orders.length})</h3>
                {orders.length > 0 && (
                  <p>
                    <Link to={`/clubhouse-payments?wannaCode=${encodeURIComponent(code)}`}>Manage refunds in Payments</Link>
                  </p>
                )}
                {orders.length === 0 ? <p>None</p> : (
                  <article className="data-table-wrap">
                    <table className="data-lists component-data-table cell-content-align-top">
                      <thead>
                        <tr>
                          <td>Order</td>
                          <td>Player</td>
                          <td>Status</td>
                          <td>Amount</td>
                          <td>Error</td>
                          <td>Created</td>
                          <td>Updated</td>
                          <td>Links</td>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <tr key={order.orderHandle}>
                            <td>{order.orderHandle}</td>
                            <td>{order.userId}</td>
                            <td>{order.status}</td>
                            <td>{formatMinor(order.amountMinor, order.currencyIsoCode)}</td>
                            <td>{order.errorText || "-"}</td>
                            <td><DisplayDate>{order.createdAtUtc}</DisplayDate></td>
                            <td><DisplayDate>{order.updatedAtUtc}</DisplayDate></td>
                            <td>{externalLink(order.frisbiiUrl)} {order.sessionUrl && externalLink(order.sessionUrl, "session")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </article>
                )}

                <h3>Transactions (charges and refunds)</h3>
                {data.transactions.length === 0 ? <p>None</p> : (
                  <article className="data-table-wrap">
                    <table className="data-lists component-data-table cell-content-align-top">
                      <thead>
                        <tr>
                          <td>Kind</td>
                          <td>Status</td>
                          <td>Amount</td>
                          <td>External reference</td>
                          <td>Error</td>
                          <td>Created</td>
                          <td>Settled</td>
                          <td>Link</td>
                        </tr>
                      </thead>
                      <tbody>
                        {data.transactions.map((transaction, index) => (
                          <tr key={transaction.externalReference || index}>
                            <td>{transaction.kind}</td>
                            <td>{transaction.status}</td>
                            <td>{formatMinor(transaction.amountMinor, transaction.currencyIsoCode)}</td>
                            <td>{transaction.externalReference || "-"}</td>
                            <td>{transaction.errorText || "-"}</td>
                            <td><DisplayDate>{transaction.createdAtUtc}</DisplayDate></td>
                            <td>{transaction.settledAtUtc ? <DisplayDate>{transaction.settledAtUtc}</DisplayDate> : "-"}</td>
                            <td>{externalLink(transaction.frisbiiUrl)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </article>
                )}

                <h3>Comments ({data.comments.length})</h3>
                {data.comments.length === 0 ? <p>None</p> : (
                  <ul>
                    {data.comments.map((comment) => (
                      <li key={comment.id}>
                        <strong>{comment.authorName}</strong> <DisplayDate>{comment.createdAtUtc}</DisplayDate>: {comment.text}
                      </li>
                    ))}
                  </ul>
                )}
              </React.Fragment>
            )}
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const Wanna = withRouter(
  withApi(
    connect(
      ({ user }) => ({ isLoggedIn: !!user }),
      {}
    )(WannaInternal)
  )
);

Wanna.requiresLogin = true;
Wanna.propTypes = { isLoggedIn: PropTypes.bool };

export default Wanna;
